import { Canceler } from 'axios';
import cloneDeep from 'lodash.clonedeep';
import { action, makeObservable, observable } from 'mobx';
import moment from 'moment';
import { defaultCustomerProfile, defaultCustomerProfileValidation } from '../constants';
import { GENDER, ICustomerProfile, ICustomerProfileValidation } from '../types';
import { SCREENS } from 'src/constants/screens';
import client from 'src/services/client';
import { history } from 'src/services/history';
import { getCancelToken } from 'src/utils/helpers';
import { emailValidation } from 'src/utils/validation';
import { ICalendarSubmitDate } from 'src/view/components/Calendar/types';
import { CUSTOMER_WALLET_MODAL_MODE } from 'src/view/containers/Modals/CustomerWalletModal/constants';
import { dateFormatForRequest } from '~/constants/dateTime';
import { localeStore } from '~/mobx/localesStore';
import logger from '~/services/logger';
import { toastSuccess } from '~/utils/toasts';

class CustomerProfileEditStore {
  constructor() {
    makeObservable(this);
  }

  cancelStack: {
    [key: string]: Canceler;
  } = {};

  @observable loading: boolean = false;

  @observable customer: ICustomerProfile = defaultCustomerProfile;
  @observable edited: boolean = false;

  public initStore = () => {
    this.loading = false;
    this.edited = false;
    this.customer = defaultCustomerProfile;
    this.validation = { ...defaultCustomerProfileValidation };
  };

  @action setCustomer = (customer?: ICustomerProfile) => {
    this.customer = customer || defaultCustomerProfile;
  };

  @action getCustomer = async (id: string | number) => {
    try {
      const cancelId = 'customer-profile';
      if (this.cancelStack[cancelId]) this.cancelStack[cancelId]('Cancelled');
      this.loading = true;

      const response: ICustomerProfile = await client.get(`v3/crm/customers/${id}`, {
        headers: { 'Accept-Language': 'en,ar' },
        cancelToken: getCancelToken((c) => (this.cancelStack[cancelId] = c)),
      });

      this.setCustomer(response);
      this.loading = false;
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.loading = false;
      logger.error({ error, place: 'CustomerProfileEditStore getCustomer', showModal: true });
    }
  };

  @action editCustomerHandler = (value: Partial<ICustomerProfile>) => {
    if (!this.customer) return;

    this.customer = { ...this.customer, ...value };
    this.edited = true;

    // update validation
    if (!this.validation.isValid) this.setValidation();
  };

  @action editCustomerBirthday = ({ startDate }: ICalendarSubmitDate) => {
    this.editCustomerHandler({ birth_day: startDate ? moment(startDate).format(dateFormatForRequest) : undefined });
  };

  @action editCustomerGender = (gender: GENDER) => {
    this.editCustomerHandler({ gender: this.customer?.gender === gender ? null : gender });
  };

  @action updateWallet = (amount: number, mode: CUSTOMER_WALLET_MODAL_MODE) => {
    if (!this.customer || !amount) return;

    const newWalletValue: number = Number(this.customer.wallet) + (mode === CUSTOMER_WALLET_MODAL_MODE.TOP_UP ? amount : -amount);

    this.customer.wallet = newWalletValue > 0 ? newWalletValue : 0;
  };

  @action updateCustomer = async () => {
    try {
      if (!this.customer) return;

      const cancelId = 'customer-update';
      if (this.cancelStack[cancelId]) this.cancelStack[cancelId]('Cancelled');

      this.loading = true;

      await client.put(`v3/crm/customers/${this.customer.id}`, this.params, { cancelToken: getCancelToken((c) => (this.cancelStack[cancelId] = c)) });

      this.edited = false;
      history.push(SCREENS.CRM_PROFILES());

      toastSuccess({ message: localeStore.t('toast_customer_profile_changes_saved') });
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.loading = false;
      logger.error({ error, place: 'CustomerProfileEditStore updateCustomer', showModal: true });
    }
  };

  get params() {
    if (!this.customer) return {};

    return {
      id: this.customer.id,
      name: this.customer.name || null,
      mobile: this.customer.mobile,
      birthday: this.customer.birth_day || null,
      email: this.customer.email || null,
      gender: this.customer.gender || null,
      status: this.customer.status,
    };
  }

  @observable validation: ICustomerProfileValidation = cloneDeep(defaultCustomerProfileValidation);
  @action setValidation = () => {
    this.validation = cloneDeep(defaultCustomerProfileValidation);

    // email validation
    if (this.customer?.email) {
      this.validation.email = emailValidation(this.customer.email);
    }

    if (!this.validation.email.isValid) this.validation.isValid = false;
  };
}

export const customerProfileEditStore = new CustomerProfileEditStore();
